import {
	ApproveMPPSLIKPayload,
	SubmitManualSlikPayload,
	useApproveMPPSLIKMutation,
	useMPPSLIKResult,
	useSubmitManualMPPSlikMutation,
} from '@/libs/fleet-api/services/mpp';
import AnalisaSLIKManual from './AnalisaSLIKManual';
import { ArrowLeftOutlined, LoadingOutlined } from '@ant-design/icons';
import { FC, Fragment, useState, useEffect, useMemo } from 'react';
import AnalisaSLIKRobotik from './AnalisaSLIKRobotic';
import {
	Alert,
	Button,
	Divider,
	Form,
	Input,
	Result,
	Select,
	Table,
} from 'antd';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import rule from '@/libs/antd/rules';
import { useForm } from '@/libs/antd/form';
import { useModals } from '@/components/modals/ModalProvider';
import { useApp } from '@/App';

const HistorySLIKMPPPage: FC<{
	mppSlikInquiryId: number;
	isReviewing?: boolean;
	onBack?: () => void;
}> = ({ mppSlikInquiryId, isReviewing = false, onBack }) => {
	const { navigate } = useApp();
	const { modal } = useModals();
	const { form, fieldName } = useForm<
		ApproveMPPSLIKPayload & SubmitManualSlikPayload
	>();
	const submitManualMPPSlikMutation = useSubmitManualMPPSlikMutation();
	const approveMPPSLIKMutation = useApproveMPPSLIKMutation();

	// State for selected company indexes in robotic SLIK - initialize with loading state
	const [selectedCompanyIndexes, setSelectedCompanyIndexes] = useState<
		number[]
	>([]);
	const [selectedShareholderIndexes, setSelectedShareholderIndexes] = useState<
		{ secondIndex: number; referenceNumber: string }[]
	>([]);

	// Track if selected indexes were initialized from API
	const [isInitializedFromAPI, setIsInitializedFromAPI] = useState(false);
	const [hasInitializedIndexes, setHasInitializedIndexes] = useState(false);

	// Memoize the query ID to prevent unnecessary re-renders
	const queryId = useMemo(() => {
		return mppSlikInquiryId && mppSlikInquiryId > 0 ? mppSlikInquiryId : null;
	}, [mppSlikInquiryId]);

	// Only call the API if we have a valid inquiry ID
	const { isFetching, data } = useMPPSLIKResult(queryId ?? undefined);

	// Debug logging for API calls
	useEffect(() => {
		if (isFetching) {
			console.log('🔍 Fetching SLIK result for ID:', queryId);
		} else if (data) {
			console.log('✅ SLIK result loaded for ID:', queryId);
		}
	}, [isFetching, data, queryId]);

	// Reset state when inquiry ID changes
	useEffect(() => {
		if (mppSlikInquiryId && mppSlikInquiryId > 0) {
			setSelectedCompanyIndexes([]);
			setSelectedShareholderIndexes([]);
			setIsInitializedFromAPI(false);
			setHasInitializedIndexes(false);
		}
	}, [mppSlikInquiryId]);

	const [detail, prospectDetail] = data ?? [];
	const {
		isManualSlik,
		manualSlikResult,
		roboticSlikResult,
		roboticSlikRequestResult,
		customerMainInformation,
		ccaNotes,
	} = detail ?? {};

	const { managementSlikInformation, shareholderSlikInformation } =
		prospectDetail ?? {};

	// Memoize the details for useEffect dependency
	const details = useMemo(() => {
		if (
			data &&
			Array.isArray(data) &&
			data[0]?.roboticSlikRequestResult?.details
		) {
			return data[0].roboticSlikRequestResult.details;
		}
		return null;
	}, [data]);

	// Initialize selected indexes from API response when data is available - only once
	useEffect(() => {
		if (data && details && !hasInitializedIndexes && !isFetching) {
			console.log('🔄 Initializing selected indexes from API response...');
			let hasSelectedIndexes = false;

			// Set company indexes for Customers role
			const customerDetail = details.find(
				(detail) => detail.role === 'Customers',
			);
			if (customerDetail?.selectedIndexes) {
				console.log(
					'📋 Found customer selectedIndexes:',
					customerDetail.selectedIndexes,
				);
				const indexes = customerDetail.selectedIndexes
					.split(',')
					.map((index: string) => parseInt(index.trim(), 10))
					.filter((index: number) => !isNaN(index));
				console.log('✅ Parsed customer indexes:', indexes);
				setSelectedCompanyIndexes(indexes);
				hasSelectedIndexes = true;
			}

			// Set shareholder indexes for non-Customer roles that have selectedIndexes
			const shareholderDetails = details.filter(
				(detail) => detail.role !== 'Customers' && detail.selectedIndexes,
			);

			const shareholderIndexes: {
				secondIndex: number;
				referenceNumber: string;
			}[] = [];

			shareholderDetails.forEach((detail) => {
				if (detail.selectedIndexes) {
					console.log(
						'📋 Found shareholder selectedIndexes:',
						detail.selectedIndexes,
						'for role:',
						detail.role,
					);
					const indexes = detail.selectedIndexes
						.split(',')
						.map((index: string) => parseInt(index.trim(), 10))
						.filter((index: number) => !isNaN(index));

					indexes.forEach((index: number) => {
						shareholderIndexes.push({
							secondIndex: index,
							referenceNumber: detail.referenceNumber,
						});
					});
				}
			});

			if (shareholderIndexes.length > 0) {
				hasSelectedIndexes = true;
				console.log('✅ Set shareholder indexes:', shareholderIndexes);
			}

			// Batch state updates to prevent multiple re-renders
			setSelectedShareholderIndexes(shareholderIndexes);
			setIsInitializedFromAPI(hasSelectedIndexes);
			setHasInitializedIndexes(true);
			console.log(
				'🎯 Initialization complete. hasSelectedIndexes:',
				hasSelectedIndexes,
			);
		}
	}, [details, hasInitializedIndexes, isFetching, data]);

	// Cleanup effect for debugging
	useEffect(() => {
		return () => {
			console.log('🧹 HistorySLIKMPPPage cleanup for ID:', mppSlikInquiryId);
		};
	}, [mppSlikInquiryId]);

	if (!mppSlikInquiryId || mppSlikInquiryId <= 0) {
		return (
			<div style={{ textAlign: 'center', padding: '40px' }}>
				<Alert
					message="Invalid SLIK Inquiry ID"
					description="No valid SLIK inquiry ID provided. Please navigate back and try again."
					type="error"
					showIcon
				/>
			</div>
		);
	}

	if (
		isFetching ||
		(!hasInitializedIndexes && data && roboticSlikRequestResult)
	) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	const isManual = Boolean(isManualSlik && manualSlikResult);
	const isRobotic = Boolean(roboticSlikResult && roboticSlikRequestResult);

	// Check if robotic SLIK is done but details are empty
	const isRoboticDoneButEmpty =
		isRobotic &&
		roboticSlikRequestResult?.status === 'DONE' &&
		(!roboticSlikResult || roboticSlikResult.length === 0);

	// Show error page if robotic SLIK is done but details are empty
	if (isRoboticDoneButEmpty) {
		try {
			const cbasIds =
				roboticSlikRequestResult?.details
					?.map((detail: { cbasId: string }) => detail.cbasId)
					.filter(Boolean) || [];

			return (
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						minHeight: '400px',
						padding: '40px',
						textAlign: 'center',
					}}
				>
					<Result
						status="warning"
						title={
							<div style={{ fontSize: 24, marginBottom: 16 }}>
								Data SLIK Belum Tersedia
							</div>
						}
						subTitle={
							<div>
								<p style={{ marginBottom: 20, fontSize: 16 }}>
									Slik robotik detail belum tersedia dari CBAS. Mohon crosscek
									dengan referensi CBAS ID:
								</p>
								{cbasIds.length > 0 ? (
									<div style={{ marginTop: '16px' }}>
										<Table
											dataSource={roboticSlikRequestResult.details
												.map((detail: { cbasId: string }) => detail.cbasId)
												.filter(Boolean)
												.map((cbasId: string, index: number) => {
													const detailInfo =
														roboticSlikRequestResult.details.find(
															(d: {
																cbasId: string;
																referenceNumber?: string;
															}) => d.cbasId === cbasId,
														);
													return {
														key: index,
														no: index + 1,
														cbasId,
														referenceNumber:
															detailInfo?.referenceNumber || 'N/A',
													};
												})}
											columns={[
												{
													title: 'No',
													dataIndex: 'no',
													key: 'no',
													width: 60,
													align: 'center',
												},
												{
													title: 'CBAS ID',
													dataIndex: 'cbasId',
													key: 'cbasId',
													align: 'center',
												},
												{
													title: 'Reference Number',
													dataIndex: 'referenceNumber',
													key: 'referenceNumber',
													align: 'center',
												},
											]}
											size="small"
											pagination={false}
											bordered
											style={{ marginTop: '8px' }}
										/>
									</div>
								) : (
									<p style={{ color: '#666', marginTop: '16px' }}>
										Tidak ada referensi CBAS ID yang tersedia
									</p>
								)}
							</div>
						}
					/>
				</div>
			);
		} catch (error) {
			console.error('Error rendering SLIK empty data page:', error);
			// Fallback to simple error message to prevent Sentry crash
			return (
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						minHeight: '400px',
						padding: '40px',
						textAlign: 'center',
					}}
				>
					<h2 style={{ color: '#faad14', marginBottom: '16px' }}>
						Data SLIK Belum Tersedia
					</h2>
					<p style={{ color: '#666', marginBottom: '20px' }}>
						Slik robotik detail belum tersedia dari CBAS.
					</p>
					<p style={{ color: '#666', marginBottom: '16px' }}>
						Mohon crosscek dengan referensi CBAS ID:
					</p>
					{roboticSlikRequestResult?.details &&
						roboticSlikRequestResult.details.length > 0 && (
							<div>
								<Table
									dataSource={roboticSlikRequestResult.details
										.map((detail: { cbasId: string }) => detail.cbasId)
										.filter(Boolean)
										.map((cbasId: string, index: number) => {
											const detailInfo = roboticSlikRequestResult.details.find(
												(d: { cbasId: string; referenceNumber?: string }) =>
													d.cbasId === cbasId,
											);
											return {
												key: index,
												no: index + 1,
												cbasId,
												referenceNumber: detailInfo?.referenceNumber || 'N/A',
											};
										})}
									columns={[
										{
											title: 'No',
											dataIndex: 'no',
											key: 'no',
											width: 60,
											align: 'center',
										},
										{
											title: 'CBAS ID',
											dataIndex: 'cbasId',
											key: 'cbasId',
											align: 'center',
										},
										{
											title: 'Reference Number',
											dataIndex: 'referenceNumber',
											key: 'referenceNumber',
											align: 'center',
										},
									]}
									size="small"
									pagination={false}
									bordered
									style={{ marginTop: '8px' }}
								/>
							</div>
						)}
					<p style={{ color: '#666', marginTop: '20px' }}>
						Mohon hubungi administrator untuk informasi lebih lanjut.
					</p>
				</div>
			);
		}
	}

	const renderSLIK = () => {
		if (isManual) {
			return (
				<AnalisaSLIKManual
					result={manualSlikResult!}
					mainInformation={customerMainInformation}
					notes={ccaNotes ?? ''}
					onBack={onBack}
				/>
			);
		}

		if (isRobotic) {
			return (
				<AnalisaSLIKRobotik
					data={roboticSlikResult!}
					result={roboticSlikRequestResult!.details}
					managementSlikInformation={managementSlikInformation ?? []}
					shareholderSlikInformation={shareholderSlikInformation ?? []}
					selectedCompanyIndexes={selectedCompanyIndexes}
					selectedShareholderIndexes={selectedShareholderIndexes}
					onCompanyIndexesChange={setSelectedCompanyIndexes}
					onShareholderIndexesChange={setSelectedShareholderIndexes}
				/>
			);
		}

		return null;
	};

	return (
		<Fragment>
			{onBack && (
				<Button
					onClick={onBack}
					style={{ marginBottom: 16 }}
				>
					<ArrowLeftOutlined />
					Back
				</Button>
			)}
			{renderSLIK()}
			{isReviewing && (
				<Form
					{...ANTD_DEFAULT_PROPS.FORM}
					form={form}
					labelCol={{ span: 6 }}
					wrapperCol={{ span: 18 }}
					onFinish={async (values) => {
						if (isManual) {
							// Skip confirmation if selected indexes were initialized from API
							if (!isInitializedFromAPI) {
								const isConfirmed = await modal.confirm({
									title:
										'Apakah Anda yakin dengan data yang telah Anda inputkan?',
								});

								if (!isConfirmed) return;
							}

							try {
								const success = await approveMPPSLIKMutation.mutateAsync({
									...values,
									mppSlikInquiryId,
								});

								if (success) {
									await modal.success({
										title: 'Manual SLIK berhasil disubmit!',
										subTitle:
											'Data SLIK manual telah berhasil dikirim untuk diproses.',
										okText: 'OK',
									});
								}
								navigate('MPPListPage');
							} catch (error: unknown) {
								console.error('Manual SLIK submission error:', error);

								let errorMessage =
									'Terjadi kesalahan saat submit SLIK manual. Silakan coba lagi.';

								if ((error as { status?: number })?.status === 400) {
									errorMessage =
										'Data SLIK tidak valid. Mohon periksa kembali data yang diinput.';
								} else if ((error as { status?: number })?.status === 409) {
									errorMessage =
										'Terjadi konflik data. SLIK mungkin sedang diproses oleh user lain.';
								}

								await modal.confirm({
									title: 'Gagal Submit SLIK Manual',
									subTitle: errorMessage,
									okText: 'OK',
									cancelButtonProps: { style: { display: 'none' } },
								});
							}
						}

						if (isRobotic) {
							// Skip confirmation if selected indexes were initialized from API
							if (!isInitializedFromAPI) {
								const isConfirmed = await modal.confirm({
									title: 'Apakah hasil summary SLIK sudah sesuai?',
								});

								if (!isConfirmed) return;
							}

							// Use selected indexes from the component, with fallbacks for single companies
							let finalSelectedCompanyIndexes = selectedCompanyIndexes;
							let finalSelectedShareholderIndexes =
								selectedShareholderIndexes.map((item) => ({
									referenceNumber: item.referenceNumber,
									secondIndexes: [item.secondIndex],
								}));

							// If no companies selected, auto-select based on available companies
							if (
								finalSelectedCompanyIndexes.length === 0 &&
								roboticSlikResult &&
								roboticSlikResult.length > 0
							) {
								if (roboticSlikResult[0].RPT_SUMMARY_COMPANY.length === 1) {
									finalSelectedCompanyIndexes = [0];
								}
							}

							// For shareholders, set to empty array since we don't have shareholder company selection yet
							// This can be enhanced later when shareholder company selection is implemented
							finalSelectedShareholderIndexes = [];

							try {
								await approveMPPSLIKMutation.mutateAsync({
									...values,
									mppSlikInquiryId,
									selectedCompanyIndexes:
										finalSelectedCompanyIndexes.length > 0
											? finalSelectedCompanyIndexes
											: undefined,
									selectedShareholderIndexes:
										finalSelectedShareholderIndexes.length > 0
											? finalSelectedShareholderIndexes
											: undefined,
								});
								await modal.success({
									title: 'Approval SLIK berhasil disubmit!',
									subTitle:
										'Hasil approval SLIK telah berhasil dikirim untuk diproses.',
									okText: 'OK',
								});
								navigate('MPPListPage');
							} catch (error: unknown) {
								console.error('SLIK approval submission error:', error);

								let errorMessage =
									'Terjadi kesalahan saat submit approval SLIK. Silakan coba lagi.';

								if ((error as { status?: number })?.status === 400) {
									errorMessage =
										'Data approval tidak valid. Mohon periksa kembali pilihan approval.';
								} else if ((error as { status?: number })?.status === 409) {
									errorMessage =
										'Terjadi konflik data. SLIK mungkin sedang diproses oleh user lain.';
								}

								await modal.confirm({
									title: 'Gagal Submit Approval SLIK',
									subTitle: errorMessage,
									okText: 'OK',
									cancelButtonProps: { style: { display: 'none' } },
								});
							}
						}
					}}
				>
					<Divider />
					<Form.Item
						name={fieldName('mppSlikInquiryId')}
						initialValue={mppSlikInquiryId}
						noStyle
					/>
					<Form.Item
						name={fieldName('action')}
						label="Approval"
						rules={[rule.required()]}
					>
						<Select
							style={{ width: 200 }}
							options={[
								{
									label: 'Approve',
									value: 'APPROVE',
								},
								{
									label: 'Reject',
									value: 'REJECT',
								},
							]}
						/>
					</Form.Item>
					<Divider />
					<Form.Item
						name={fieldName('notes')}
						label="Notes"
						rules={[rule.required()]}
					>
						<Input.TextArea rows={4} />
					</Form.Item>

					<Button
						type="primary"
						htmlType="submit"
						style={{
							display: 'block',
							width: 200,
							marginLeft: 'auto',
							marginTop: 40,
						}}
						loading={
							submitManualMPPSlikMutation.isPending ||
							approveMPPSLIKMutation.isPending
						}
						disabled={
							submitManualMPPSlikMutation.isPending ||
							approveMPPSLIKMutation.isPending
						}
					>
						SAVE
					</Button>
				</Form>
			)}
		</Fragment>
	);
};

export default HistorySLIKMPPPage;
